import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import type { RetrySubscriptionPaymentResponseDto } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getStripePromise } from 'app/http/stripeConfig';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useEffect, useRef } from 'react';
import { mapRetryPaymentError } from './mapRetryPaymentError';

export type TUseRetrySubscriptionPaymentOptions = {
  retry: () => Promise<RetrySubscriptionPaymentResponseDto>;
  onPaid: () => void;
  onUpdatePaymentMethod?: () => void;
};

type TErrorDetails = {
  code?: string;
  message?: string;
};

const getApiCallErrorDetails = (error: ApiCallError): TErrorDetails => {
  if (typeof error.body !== 'object' || error.body === null) {
    return {};
  }

  const code = 'error' in error.body && typeof error.body.error === 'string' ? error.body.error : undefined;
  const message = 'message' in error.body && typeof error.body.message === 'string' ? error.body.message : undefined;

  return { code, message };
};

export const useRetrySubscriptionPayment = ({
  retry,
  onPaid,
  onUpdatePaymentMethod,
}: TUseRetrySubscriptionPaymentOptions) => {
  const { openModal } = useModalContext();
  const hasRetriedAfterAction = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const openFailureModal = (details: TErrorDetails = {}) => {
    openModal(ModalNamesEnum.Status, {
      buttonMessage: 'Ok',
      message: mapRetryPaymentError(details),
      ...(onUpdatePaymentMethod
        ? {
            secondaryAction: {
              label: 'Update payment method',
              onClick: onUpdatePaymentMethod,
            },
          }
        : {}),
      status: 'error',
      title: 'Payment failed',
    });
  };

  const handleResponse = async (response: RetrySubscriptionPaymentResponseDto) => {
    if (!isMounted.current) {
      return;
    }

    switch (response?.outcome) {
      case 'paid':
        onPaid();
        openModal(ModalNamesEnum.Status, {
          buttonMessage: 'Ok',
          message: 'Your payment went through. Your AutoScript One subscription is active again.',
          status: 'success',
          title: 'Successful payment',
        });
        return;
      case 'processing':
        onPaid();
        openModal(ModalNamesEnum.Status, {
          buttonMessage: 'Ok',
          message: 'Your payment is processing. The subscription will activate automatically once it settles.',
          status: 'pending',
          title: 'Payment processing',
        });
        return;
      case 'requires_action': {
        if (!response.paymentIntentClientSecret || hasRetriedAfterAction.current) {
          openFailureModal();
          return;
        }

        const stripe = await getStripePromise().catch(() => null);
        if (!isMounted.current) {
          return;
        }
        if (!stripe) {
          openFailureModal();
          return;
        }

        const result = await stripe
          .handleNextAction({ clientSecret: response.paymentIntentClientSecret })
          .catch(() => ({ error: { message: undefined } }));
        if (!isMounted.current) {
          return;
        }
        if (result.error) {
          openFailureModal({ message: result.error.message });
          return;
        }

        hasRetriedAfterAction.current = true;
        retryMutation.mutate();
        return;
      }
      case 'failed':
      default:
        openFailureModal({ message: response?.error });
    }
  };

  const retryMutation = useMutation({
    mutationFn: () => retry(),
    onError: (error: unknown) => {
      if (!isMounted.current) {
        return;
      }
      openFailureModal(error instanceof ApiCallError ? getApiCallErrorDetails(error) : undefined);
    },
    onSuccess: handleResponse,
  });

  const retryPayment = () => {
    hasRetriedAfterAction.current = false;
    retryMutation.mutate();
  };

  return { isPending: retryMutation.isPending, retryPayment };
};
